import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { PAYMENT_FLOWS, RATE_SOURCES, TRANSACTION_TYPES } from '@subs/shared';

import {
  bills,
  duplicateSourceHashConflict,
  moneyAccounts,
  payments,
  subscriptions,
  type DuplicateSourceHashConflict,
  type PaymentEvent,
  type PaymentInput,
  type TransactionInput,
} from '../api/client';
import { useToast } from './Toast';
import { Button, Field, Input, Select, Textarea } from './ui';
import { formatAmount, formatBytes, formatDateTime } from '../lib/format';

/** `<input type="datetime-local">` value (local wall clock) for an ISO stamp. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Human label for a leg type — "Card top-up" is the owner's word for a funding. */
const LEG_TYPE_LABEL: Record<(typeof TRANSACTION_TYPES)[number], string> = {
  PAYMENT: 'Payment',
  CONVERSION: 'Conversion',
  TOPUP: 'Card top-up',
};

interface FormState {
  subscriptionId: string;
  occurredAt: string;
  flow: (typeof PAYMENT_FLOWS)[number];
  paidAmount: string;
  paidCurrency: string;
  note: string;
  legType: (typeof TRANSACTION_TYPES)[number];
  accountId: string;
  legAmount: string;
  legCurrency: string;
  fxRate: string;
  rateSource: (typeof RATE_SOURCES)[number];
  rubAmount: string;
  commission: string;
  sourceTxnNo: string;
  /** Edit mode only: the account each stored leg is booked on, by leg id. */
  legAccounts: Record<string, string>;
  /** Edit mode only: the type each stored leg carries, by leg id. */
  legTypes: Record<string, (typeof TRANSACTION_TYPES)[number]>;
}

/**
 * What a caller may pre-fill in create mode — today only the forecast drawer's
 * "Register payment now", which starts from the forecast's own amount and
 * renewal date. The account is deliberately absent: the forecast knows nothing
 * about money accounts, and guessing one would mis-book the leg.
 */
export interface PaymentPrefill {
  subscriptionId: string;
  amount: number;
  currency: string;
  /** ISO stamp the date/time field starts from. */
  occurredAt: string;
}

function fromPrefill(prefill: PaymentPrefill): FormState {
  const currency = prefill.currency.trim().toUpperCase() || EMPTY.paidCurrency;
  return {
    ...EMPTY,
    subscriptionId: prefill.subscriptionId,
    occurredAt: toLocalInput(prefill.occurredAt) || EMPTY.occurredAt,
    paidAmount: String(prefill.amount),
    paidCurrency: currency,
    legAmount: String(prefill.amount),
    legCurrency: currency,
  };
}

function fromEvent(event: PaymentEvent): FormState {
  const leg = event.transactions[0];
  return {
    subscriptionId: event.subscriptionId,
    occurredAt: toLocalInput(event.occurredAt),
    flow: event.flow,
    paidAmount: String(event.paidAmount),
    paidCurrency: event.paidCurrency,
    note: event.note ?? '',
    legType: leg?.type ?? 'PAYMENT',
    accountId: leg?.accountId ?? '',
    legAmount: leg ? String(leg.amount) : '',
    legCurrency: leg?.currency ?? event.paidCurrency,
    fxRate: leg?.fxRate !== null && leg?.fxRate !== undefined ? String(leg.fxRate) : '',
    rateSource: leg?.rateSource ?? 'MANUAL',
    rubAmount: leg?.rubAmount !== null && leg?.rubAmount !== undefined ? String(leg.rubAmount) : '',
    commission: leg?.commission !== null && leg?.commission !== undefined ? String(leg.commission) : '',
    sourceTxnNo: leg?.sourceTxnNo ?? '',
    legAccounts: Object.fromEntries(event.transactions.map((txn) => [txn.id, txn.accountId])),
    legTypes: Object.fromEntries(event.transactions.map((txn) => [txn.id, txn.type])),
  };
}

const EMPTY: FormState = {
  subscriptionId: '',
  occurredAt: toLocalInput(new Date().toISOString()),
  flow: 'DIRECT',
  paidAmount: '',
  paidCurrency: 'USD',
  note: '',
  legType: 'PAYMENT',
  accountId: '',
  legAmount: '',
  legCurrency: 'USD',
  fxRate: '',
  rateSource: 'MANUAL',
  rubAmount: '',
  commission: '',
  sourceTxnNo: '',
  legAccounts: {},
  legTypes: {},
};

function validate(state: FormState, editing: boolean): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!editing && !state.subscriptionId) errors.subscriptionId = 'Pick the subscription';
  if (!state.occurredAt) errors.occurredAt = 'When was it paid?';
  if (!state.paidAmount.trim() || Number(state.paidAmount) <= 0) errors.paidAmount = 'Amount above zero is required';
  if (state.paidCurrency.trim().length !== 3) errors.paidCurrency = '3-letter code';
  // A leg is only created here: editing keeps the stored legs (and the bills
  // booked on them) intact and only ever re-points an account or a type.
  if (!editing) {
    if (!state.accountId) errors.accountId = 'Pick the account the leg was booked on';
    if (!state.legAmount.trim() || Number(state.legAmount) <= 0) errors.legAmount = 'Leg amount above zero is required';
  } else {
    for (const [legId, accountId] of Object.entries(state.legAccounts)) {
      if (!accountId) errors[`leg-${legId}`] = 'Pick the account this leg was booked on';
    }
  }
  return errors;
}

const num = (value: string): number | undefined => {
  const parsed = Number(value);
  return value.trim() === '' || Number.isNaN(parsed) ? undefined : parsed;
};

/** Mirror of the API's own bill rules (`bills/bill-store.ts`) — a fast "no". */
const BILL_ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp';
const BILL_MAX_BYTES = 20 * 1024 * 1024;

/** `undefined` means "let the API decide": the limits are its source of truth. */
export function billProblem(file: File): string | undefined {
  if (file.size === 0) return 'That file is empty';
  if (file.size > BILL_MAX_BYTES) return `That file is over 20 MB (${formatBytes(file.size)})`;
  if (file.type && !BILL_ACCEPT.split(',').includes(file.type)) return `Unsupported file type ${file.type}`;
  return undefined;
}

/**
 * One bill per leg, chosen while recording the payment. Nothing is uploaded
 * until the payment exists: `POST /bills` needs a `transactionId`, and the leg
 * id is only known after the event is created.
 */
function LegBillField({
  file,
  error,
  onPick,
}: {
  file: File | null;
  error?: string;
  onPick: (file: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="form-grid__full">
      <div className="field">
        <span className="field__label">
          Attach bill
          <span className="field__hint"> optional — PDF or screenshot, up to 20 MB, attached to this leg</span>
        </span>
        {file ? (
          <div className="leg-bill">
            <span className="leg-bill__name">{file.name}</span>
            <span className="leg-bill__size">{formatBytes(file.size)}</span>
            <Button variant="ghost" size="sm" onClick={() => onPick(null)}>
              Remove
            </Button>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => inputRef.current?.click()}>
            Choose a bill for this leg
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={BILL_ACCEPT}
          hidden
          onChange={(event) => {
            const picked = event.target.files?.[0] ?? null;
            onPick(picked);
            event.target.value = '';
          }}
        />
        {error ? <p className="field__error" role="alert">{error}</p> : null}
      </div>
    </div>
  );
}

/**
 * The receipt-dedup 409, rendered where the user can act on it. The button is
 * offered only for a receipt-backed collision — a receipt-less leg cannot
 * collide any more (no receipt № means no dedup anchor), so a conflict on one
 * would mean something else went wrong and "record anyway" would be guessing.
 */
function DuplicateNotice({
  conflict,
  onRecordAnyway,
  busy,
}: {
  conflict: DuplicateSourceHashConflict;
  onRecordAnyway: () => void;
  busy?: boolean;
}) {
  const leg = conflict.conflicting;
  const payment = leg.payment;
  const receiptBacked = Boolean(leg.sourceTxnNo);

  return (
    <div className="dup-conflict" role="alert">
      <p className="dup-conflict__title">
        This receipt is already recorded{receiptBacked ? ` — №${leg.sourceTxnNo}` : ''}
      </p>
      <dl className="dl">
        {payment?.subscriptionName ? (
          <>
            <dt>Subscription</dt>
            <dd>{payment.subscriptionName}</dd>
          </>
        ) : null}
        <dt>Recorded</dt>
        <dd>{formatDateTime(payment?.occurredAt ?? leg.occurredAt)}</dd>
        <dt>Amount</dt>
        <dd>
          {formatAmount(leg.amount, leg.currency)}
          {payment && (payment.paidCurrency !== leg.currency || payment.paidAmount !== leg.amount) ? (
            <> of {formatAmount(payment.paidAmount, payment.paidCurrency)}</>
          ) : null}
        </dd>
        {leg.account ? (
          <>
            <dt>Account</dt>
            <dd>
              {leg.account.name} ({leg.account.currency})
            </dd>
          </>
        ) : null}
      </dl>
      {payment?.id ? (
        <p className="state__hint">
          Same receipt №, account, day and amount as payment{' '}
          <code>{payment.id.slice(0, 8)}</code>
          {payment.subscriptionName ? ` · ${payment.subscriptionName}` : ''}.
        </p>
      ) : null}
      {receiptBacked ? (
        <div className="dup-conflict__actions">
          <Button size="sm" variant="secondary" loading={busy} onClick={onRecordAnyway}>
            Record anyway
          </Button>
          <span className="state__hint">
            Stores this leg without a receipt anchor, so the two stay separate.
          </span>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Create / edit one payment event, inline in the drawer.
 *
 * Editing covers the event-level fields plus each leg's **account** and
 * **type**: `PATCH /payments/:id` would replace the transaction set wholesale
 * when `transactions` is sent and detach the bills booked on those legs, so the
 * per-leg edits go through `PATCH /transactions/:id` instead — a merge that
 * keeps the amounts, dates, rates and bills exactly as they are. Re-typing a
 * leg is how a misfiled card funding becomes a commission (there is no
 * COMMISSION value: the report splits on the absence of a PAYMENT leg).
 */
export function PaymentEventForm({
  event,
  prefill = null,
  onDone,
  onCancel,
}: {
  /** Present in edit mode, null in create mode. */
  event: PaymentEvent | null;
  /** Create-mode starting values ("Register payment now" on a forecast). */
  prefill?: PaymentPrefill | null;
  onDone: (saved: PaymentEvent, options?: { uploadFailed?: boolean }) => void;
  onCancel: () => void;
}) {
  const editing = Boolean(event);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<FormState>(() =>
    event ? fromEvent(event) : prefill ? fromPrefill(prefill) : EMPTY,
  );
  const [touched, setTouched] = useState(false);
  /** A receipt-dedup 409, kept on screen with its "Record anyway" escape hatch. */
  const [conflict, setConflict] = useState<DuplicateSourceHashConflict | null>(null);
  const [allowDuplicate, setAllowDuplicate] = useState(false);
  /** The bill to attach to the leg being created, and any pick problem. */
  const [bill, setBill] = useState<File | null>(null);
  const [billError, setBillError] = useState<string | undefined>(undefined);
  /** Survives the await: a failed upload must reach `onDone` after the save. */
  const uploadFailedRef = useRef(false);

  const set = <K extends keyof FormState>(field: K, value: FormState[K]) =>
    setState((current) => ({ ...current, [field]: value }));

  const subscriptionList = useQuery({
    queryKey: ['subscriptions', 'payment-form'],
    queryFn: ({ signal }) => subscriptions.list({ page: 1, limit: 200 }, signal),
    enabled: !editing,
    staleTime: 60_000,
  });

  // Needed in both modes: create books the new leg, edit re-points a stored one.
  const accountList = useQuery({
    queryKey: ['money-accounts', 'payment-form'],
    queryFn: ({ signal }) => moneyAccounts.list({ page: 1, limit: 200 }, signal),
    staleTime: 60_000,
  });

  const save = useMutation({
    /**
     * Create: the event first, then the bill — `POST /bills` needs the leg id
     * that only exists once the event does. A failed upload does NOT roll the
     * payment back (the money is real; the bill is a document): the error is
     * surfaced and the drawer's own dropzone becomes the retry path.
     */
    mutationFn: async (payload: Partial<PaymentInput> & { __bill?: File | null }) => {
      // `__bill` is this form's instruction, never an API field.
      const { __bill: bill, ...body } = payload;
      if (!event) {
        const created = await payments.create(body as PaymentInput);
        const legId = created.transactions[0]?.id;
        if (bill && legId) {
          try {
            await bills.upload(bill, legId);
          } catch (error) {
            // The money is already real; the bill is only a document. Keep the
            // payment, say so, and let the drawer's dropzone be the retry.
            uploadFailedRef.current = true;
            toast(
              error instanceof Error
                ? `The payment was recorded, but the bill upload failed: ${error.message}`
                : 'The payment was recorded, but the bill upload failed.',
              'error',
            );
          }
        }
        return created;
      }
      await payments.update(event.id, payload);

      const changed = (event.transactions ?? []).filter((txn) => {
        const accountId = state.legAccounts[txn.id] ?? txn.accountId;
        const type = state.legTypes[txn.id] ?? txn.type;
        return accountId !== txn.accountId || type !== txn.type;
      });
      await Promise.all(
        changed.map((txn) => {
          const accountId = state.legAccounts[txn.id] ?? txn.accountId;
          const type = state.legTypes[txn.id] ?? txn.type;
          return payments.updateTransaction(txn.id, {
            ...(accountId !== txn.accountId ? { accountId } : {}),
            ...(type !== txn.type ? { type } : {}),
          });
        }),
      );

      // The stored legs were patched behind the event's back, so the freshly
      // returned event would still carry the old account names and types.
      return payments.get(event.id);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast(event ? 'Payment updated' : 'Payment recorded', 'success');
      onDone(saved, { uploadFailed: uploadFailedRef.current });
      uploadFailedRef.current = false;
    },
    onError: (error: Error) => {
      // The 409 gets its own panel with the details and a way through; a toast
      // alone would leave the user with "duplicate" and no idea of what of.
      const duplicate = duplicateSourceHashConflict(error);
      if (duplicate) {
        setConflict(duplicate);
        return;
      }
      toast(error.message, 'error');
    },
  });

  const errors = validate(state, editing);
  const show = (field: keyof FormState) => (touched ? errors[field] : undefined);

  const submit = (forceDuplicate = false) => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    if (billError) return;
    setConflict(null);

    const occurredAt = new Date(state.occurredAt).toISOString();
    if (editing) {
      save.mutate({
        occurredAt,
        flow: state.flow,
        paidAmount: Number(state.paidAmount),
        paidCurrency: state.paidCurrency.trim().toUpperCase(),
        note: state.note.trim() || null,
      });
      return;
    }

    const leg: TransactionInput = {
      type: state.legType,
      occurredAt,
      amount: Number(state.legAmount),
      currency: state.legCurrency.trim().toUpperCase(),
      accountId: state.accountId,
    };
    const fxRate = num(state.fxRate);
    const rubAmount = num(state.rubAmount);
    const commission = num(state.commission);
    if (fxRate !== undefined) {
      leg.fxRate = fxRate;
      leg.rateSource = state.rateSource;
    }
    if (rubAmount !== undefined) leg.rubAmount = rubAmount;
    if (commission !== undefined) leg.commission = commission;
    if (state.sourceTxnNo.trim()) leg.sourceTxnNo = state.sourceTxnNo.trim();

    save.mutate({
      subscriptionId: state.subscriptionId,
      occurredAt,
      flow: state.flow,
      paidAmount: Number(state.paidAmount),
      paidCurrency: state.paidCurrency.trim().toUpperCase(),
      note: state.note.trim() || null,
      transactions: [leg],
      // Not part of the API payload — it names the file to upload afterwards.
      __bill: bill,
      // Only ever set by the "Record anyway" action on a dedup 409.
      ...(forceDuplicate || allowDuplicate ? { allowDuplicate: true } : {}),
    });
  };

  /**
   * "Record anyway": the caller asserts this really is a second, distinct
   * payment that happens to reuse the receipt №, so the leg is stored with no
   * dedup anchor at all and the same payload is sent again.
   */
  const recordAnyway = () => {
    setAllowDuplicate(true);
    setConflict(null);
    submit(true);
  };

  /**
   * A type edit is not cosmetic. The report splits subscription spend from
   * card funding by the *absence* of a PAYMENT leg, and `computeRubTotal` sums
   * only the CONVERSION/TOPUP legs whenever the event has one — so moving a leg
   * across that line can move both the ₽ total and the classification.
   */
  const typeShiftsSemantics = (event?.transactions ?? []).some((txn) => {
    const next = state.legTypes[txn.id];
    return next !== undefined && next !== txn.type && (next === 'PAYMENT') !== (txn.type === 'PAYMENT');
  });

  /**
   * The account + type pair for one stored leg. The leg's own account is always
   * an option — even before the account list resolves, so the control never
   * shows the wrong value while loading.
   */
  const legFields = (leg: PaymentEvent['transactions'][number], index: number) => {
    const known = accountList.data?.items ?? [];
    const current = leg.account
      ? { id: leg.account.id, name: leg.account.name, currency: leg.account.currency }
      : null;
    const options = current && !known.some((account) => account.id === current.id)
      ? [current, ...known]
      : known;
    const numbered = Boolean(event) && event!.transactions.length > 1;
    const accountLabel = numbered ? `Account · ${LEG_TYPE_LABEL[leg.type]} №${index + 1}` : 'Account';
    const typeLabel = numbered ? `Type · №${index + 1}` : 'Type';

    return (
      <>
        <Field
          label={accountLabel}
          htmlFor={`leg-${leg.id}-account`}
          error={touched ? errors[`leg-${leg.id}`] : undefined}
        >
          <Select
            id={`leg-${leg.id}-account`}
            value={state.legAccounts[leg.id] ?? leg.accountId}
            aria-label={accountLabel}
            onChange={(changeEvent) =>
              setState((current) => ({
                ...current,
                legAccounts: { ...current.legAccounts, [leg.id]: changeEvent.target.value },
              }))
            }
          >
            {!options.length ? <option value={leg.accountId}>{leg.accountId}</option> : null}
            {options.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name} ({account.currency})
              </option>
            ))}
          </Select>
        </Field>
        <Field label={typeLabel} htmlFor={`leg-${leg.id}-type`}>
          <Select
            id={`leg-${leg.id}-type`}
            value={state.legTypes[leg.id] ?? leg.type}
            aria-label={typeLabel}
            onChange={(changeEvent) =>
              setState((current) => ({
                ...current,
                legTypes: { ...current.legTypes, [leg.id]: changeEvent.target.value as FormState['legType'] },
              }))
            }
          >
            {TRANSACTION_TYPES.map((type) => (
              <option key={type} value={type}>
                {LEG_TYPE_LABEL[type]}
              </option>
            ))}
          </Select>
        </Field>
      </>
    );
  };

  const currencyField = (id: string, label: string, field: 'paidCurrency' | 'legCurrency') => (
    <Field label={label} htmlFor={id} required error={show(field)}>
      <Input id={id} maxLength={3} value={state[field]} onChange={(event) => set(field, event.target.value.toUpperCase())} />
    </Field>
  );

  return (
    <form
      onSubmit={(formEvent) => {
        formEvent.preventDefault();
        submit();
      }}
    >
      {editing ? (
        <p className="state__hint" style={{ marginTop: 0 }}>
          Event fields and leg account/type are editable — leg amounts, dates, rates and bills are preserved.
        </p>
      ) : null}

      {conflict ? (
        <DuplicateNotice conflict={conflict} onRecordAnyway={recordAnyway} busy={save.isPending} />
      ) : null}

      <div className="form-grid">
        {!editing ? (
          <div className="form-grid__full">
            <Field label="Subscription" htmlFor="pay-sub" required error={show('subscriptionId')}>
              <Select id="pay-sub" value={state.subscriptionId} onChange={(event) => set('subscriptionId', event.target.value)}>
                <option value="">— choose —</option>
                {(subscriptionList.data?.items ?? []).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        ) : null}

        <Field label="Paid at" htmlFor="pay-occurred" required error={show('occurredAt')}>
          <Input
            id="pay-occurred"
            type="datetime-local"
            value={state.occurredAt}
            aria-invalid={Boolean(show('occurredAt'))}
            onChange={(event) => set('occurredAt', event.target.value)}
          />
        </Field>

        <Field label="Flow" htmlFor="pay-flow" hint="A direct · B two-step at the bank · C card top-up">
          <Select id="pay-flow" value={state.flow} onChange={(event) => set('flow', event.target.value as FormState['flow'])}>
            {PAYMENT_FLOWS.map((flow) => (
              <option key={flow} value={flow}>
                {flow}
              </option>
            ))}
          </Select>
        </Field>

        <Field label="Paid amount" htmlFor="pay-amount" required error={show('paidAmount')}>
          <Input
            id="pay-amount"
            type="number"
            min="0"
            step="0.01"
            value={state.paidAmount}
            aria-invalid={Boolean(show('paidAmount'))}
            onChange={(event) => set('paidAmount', event.target.value)}
          />
        </Field>

        {currencyField('pay-currency', 'Currency', 'paidCurrency')}

        <div className="form-grid__full">
          <Field label="Note" htmlFor="pay-note">
            <Textarea id="pay-note" value={state.note} onChange={(event) => set('note', event.target.value)} />
          </Field>
        </div>

        {editing
          ? (event?.transactions ?? []).map((leg, index) => (
              <div className="form-grid__full" key={leg.id}>
                <div className="form-grid">{legFields(leg, index)}</div>
              </div>
            ))
          : null}

        {editing && typeShiftsSemantics ? (
          <div className="form-grid__full">
            <p className="state__hint" style={{ marginTop: 'var(--space-2)' }}>
              Type change affects the ₽ total and how this event is classified (subscription payment vs commission).
            </p>
          </div>
        ) : null}
      </div>

      {!editing ? (
        <>
          <h3 className="section-title">Transaction leg</h3>
          <div className="form-grid">
            <Field label="Type" htmlFor="leg-type">
              <Select id="leg-type" value={state.legType} onChange={(event) => set('legType', event.target.value as FormState['legType'])}>
                {TRANSACTION_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {LEG_TYPE_LABEL[type]}
                  </option>
                ))}
              </Select>
            </Field>

            <div className="form-grid__full">
              <Field label="Account" htmlFor="leg-account" required error={show('accountId')}>
                <Select id="leg-account" value={state.accountId} onChange={(event) => set('accountId', event.target.value)}>
                  <option value="">— choose —</option>
                  {(accountList.data?.items ?? []).map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name} ({account.currency})
                    </option>
                  ))}
                </Select>
              </Field>
            </div>

            <Field label="Leg amount" htmlFor="leg-amount" required error={show('legAmount')}>
              <Input
                id="leg-amount"
                type="number"
                min="0"
                step="0.01"
                value={state.legAmount}
                aria-invalid={Boolean(show('legAmount'))}
                onChange={(event) => set('legAmount', event.target.value)}
              />
            </Field>

            {currencyField('leg-currency', 'Leg currency', 'legCurrency')}

            <Field label="FX rate" htmlFor="leg-fx" hint="Leave empty when the leg is already in ₽">
              <Input id="leg-fx" type="number" min="0" step="0.000001" value={state.fxRate} onChange={(event) => set('fxRate', event.target.value)} />
            </Field>

            <Field label="RUB amount" htmlFor="leg-rub" hint="Filled from the bill when there is one">
              <Input id="leg-rub" type="number" min="0" step="0.01" value={state.rubAmount} onChange={(event) => set('rubAmount', event.target.value)} />
            </Field>

            <Field label="Commission" htmlFor="leg-commission">
              <Input id="leg-commission" type="number" min="0" step="0.01" value={state.commission} onChange={(event) => set('commission', event.target.value)} />
            </Field>

            <Field label="Receipt №" htmlFor="leg-txnno">
              <Input id="leg-txnno" value={state.sourceTxnNo} onChange={(event) => set('sourceTxnNo', event.target.value)} />
            </Field>

            <Field label="Rate source" htmlFor="leg-source" hint="Where the FX rate came from">
              <Select
                id="leg-source"
                value={state.rateSource}
                disabled={!state.fxRate}
                onChange={(event) => set('rateSource', event.target.value as FormState['rateSource'])}
              >
                {RATE_SOURCES.map((source) => (
                  <option key={source} value={source}>
                    {source}
                  </option>
                ))}
              </Select>
            </Field>

            <LegBillField
              file={bill}
              error={billError}
              onPick={(picked) => {
                setBillError(picked ? billProblem(picked) : undefined);
                setBill(picked);
              }}
            />
          </div>
        </>
      ) : null}

      <div className="modal__footer" style={{ padding: 'var(--space-4) 0 0' }}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary" loading={save.isPending}>
          {editing ? 'Save changes' : 'Record payment'}
        </Button>
      </div>
    </form>
  );
}
